import { CircleX, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useAuth } from '@/hooks/useAuth';
import { usePhotos } from '@/hooks/usePhotos';
import { useKeys } from '@/hooks/useKeys';
import { useUsers } from '@/hooks/useUsers';
import { api } from '@/lib/api';

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const UserPane = () => {
  const { loadUsers, getUserById } = useUsers();
  const { photos } = usePhotos();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedEmoji, setEditedEmoji] = useState('');

  const { userId } = useParams();
  const user = getUserById(userId || '');

  const isOwnProfile = currentUser?.id.toString() === userId;

  // Filter photos for this user - handle both string and number ID types
  const userPhotos = photos.filter(photo => 
    photo?.user_id?.toString() === userId
  );
  useKeys([
    ['Escape', () => onCloseClick()],
    ['e', () => isOwnProfile && !isEditMode && setIsEditMode(true)]
  ], true);

  const onCloseClick = () => { navigate('/users'); }
  const onEditClick = () => {
    if (!isEditMode) {
      setEditedName(user?.name || '');
      setEditedTitle(user?.title || '');
      setEditedDescription(user?.description || '');
      setEditedEmoji(user?.emoji || '');
    }
    setIsEditMode(!isEditMode);
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-white rounded-lg">
        <p className="text-xl text-gray-600">User not found</p>
      </div>
    );
  }

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    try {
      await api.put(`/users/${user.id}`, {
        user: {
          name: editedName,
          title: editedTitle,
          description: editedDescription,
          emoji: editedEmoji,
        },
      });
      await loadUsers();
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-white rounded-lg">
      <button
        onClick={onCloseClick}
        className="absolute z-10 flex items-center justify-center w-8 h-8 text-2xl text-white rounded-full cursor-pointer top-2 right-2 hover:opacity-75"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <CircleX className="w-8 h-8" />
      </button>
      {isOwnProfile && (
        <button
          onClick={onEditClick}
          className="absolute z-10 flex items-center justify-center w-8 h-8 text-2xl text-white rounded-full cursor-pointer top-2 left-2 hover:opacity-75"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <Pencil className="w-5 h-5" />
        </button>
      )}

      <div className="flex-1 p-6">
        {isEditMode && isOwnProfile ? (
          <div className="w-full h-full mt-12">
            <form onSubmit={handleSave} className="space-y-6">
              <div>
                <label className="block">
                  <span className="text-lg font-medium text-gray-700">Name</span>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
                    placeholder="Enter name"
                  />
                </label>
              </div>

              <div>
                <label className="block">
                  <span className="text-lg font-medium text-gray-700">Title</span>
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
                    placeholder="Enter title"
                  />
                </label>
              </div>

              <div>
                <label className="block">
                  <span className="text-lg font-medium text-gray-700">Description</span>
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
                    rows={4}
                    placeholder="Enter description"
                  />
                </label>
              </div>

              <div>
                <label className="block">
                  <span className="text-lg font-medium text-gray-700">Emoji</span>
                  <input
                    type="text"
                    value={editedEmoji}
                    onChange={(e) => setEditedEmoji(e.target.value)}
                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
                    placeholder="Enter emoji"
                  />
                </label>
              </div>

              <div className="flex justify-end pt-4 space-x-4">
                <button
                  type="button"
                  onClick={() => setIsEditMode(false)}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              {user.emoji && (
                <div className="flex items-center justify-center w-16 h-16 text-4xl bg-gray-100 rounded-full">
                  {user.emoji}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                {user.title && (
                  <p className="text-lg text-gray-600">{user.title}</p>
                )}
                {user.description && (
                  <p className="mt-2 text-gray-600">{user.description}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">Joined {formatDate(user.created_at)}</p>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="mb-4 text-xl font-semibold text-gray-900">Photos</h2>
              <div className="grid grid-cols-4 gap-4">
                {userPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    className="relative overflow-hidden transition-transform duration-200 rounded-lg shadow-md cursor-pointer aspect-square hover:scale-105"
                    onClick={() => navigate(`/photos/${photo.id}`)}
                  >
                    <img
                      src={photo.thumb_url || photo.image_url}
                      alt={photo.title}
                      className="object-cover w-full h-full"
                    />
                    <div
                      className="absolute bottom-0 left-0 right-0 p-2 text-white transition-opacity duration-200 opacity-0 hover:opacity-100"
                      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                    >
                      <p className="text-sm truncate">{photo.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
